import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, Plus, ArrowRight } from "lucide-react";
import { FactorIcon } from "@/components/icons";
import { useLang } from "@/context/LanguageContext";

export default function Understand({ understanding, factors, setFactors, onConfirm }) {
  const { t } = useLang();
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");

  const removeFactor = (id) => setFactors(factors.filter((f) => f.id !== id));
  const addFactor = () => {
    const label = newLabel.trim();
    if (!label) return;
    setFactors([...factors, { id: label.toLowerCase().replace(/\s+/g, "_") + "_" + Date.now(), icon: "other", label }]);
    setNewLabel("");
    setAdding(false);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <p className="decide-label">{t("understand.deciding")}</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-light tracking-tight text-decide-ink leading-tight" data-testid="understand-summary">
        {understanding.summary}
      </h1>

      <div className="mt-7">
        <p className="decide-label mb-3">{t("understand.options")}</p>
        <div className="grid grid-cols-1 gap-3">
          {understanding.options.map((o) => (
            <div key={o.key} className="decide-card !p-4 flex items-start gap-3" data-testid="understand-option">
              <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-decide-panel text-white text-sm font-bold">{o.key}</span>
              <div>
                <p className="font-semibold text-decide-ink">{o.label}</p>
                {o.description && <p className="text-[13px] text-decide-slate mt-0.5">{o.description}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-7">
        <p className="decide-label mb-1">{t("understand.mattersMost")}</p>
        <p className="text-[13px] text-decide-slate mb-3">{t("understand.mattersSub")}</p>
        <div className="flex flex-wrap gap-2">
          {factors.map((f) => (
            <div key={f.id} data-testid="factor-chip" className="group inline-flex items-center gap-2 rounded-full border border-decide-line bg-decide-surface pl-3 pr-2 py-2">
              <FactorIcon name={f.icon} className="h-4 w-4 text-decide-forest" />
              <span className="text-sm font-medium text-decide-ink">{f.label}</span>
              <button aria-label={`Remove ${f.label}`} data-testid="remove-factor" onClick={() => removeFactor(f.id)} className="ml-0.5 flex h-5 w-5 items-center justify-center rounded-full text-decide-mute hover:bg-decide-mist hover:text-decide-ink">
                <X size={13} />
              </button>
            </div>
          ))}
          {adding ? (
            <div className="inline-flex items-center gap-1 rounded-full border border-decide-forest bg-decide-surface pl-3 pr-1 py-1">
              <input autoFocus data-testid="new-factor-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addFactor()} placeholder={t("understand.addFactor")} className="w-24 bg-transparent text-sm focus:outline-none" />
              <button data-testid="confirm-add-factor" onClick={addFactor} className="rounded-full bg-decide-forest px-2 py-1 text-xs font-semibold text-white">{t("understand.add")}</button>
            </div>
          ) : (
            <button data-testid="add-factor-btn" onClick={() => setAdding(true)} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-decide-mute px-3 py-2 text-sm text-decide-slate hover:border-decide-ink hover:text-decide-ink transition-colors">
              <Plus size={15} /> {t("understand.add")}
            </button>
          )}
        </div>
      </div>

      <button data-testid="looks-right-btn" onClick={onConfirm} className="btn-primary mt-8 w-full">
        {t("understand.looksRight")} <ArrowRight size={18} />
      </button>
    </motion.div>
  );
}
